import { Box, Button, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement, useCallback, useState } from "react";

import {
  CancelCampaignRoiTestMutation,
  CancelCampaignRoiTestMutationVariables,
} from "~frontend/__generated__/graphql";
import { DropdownButton } from "~frontend/components/DropdownButton";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import { CANCEL_CAMPAIGN_ROI_TEST } from "~frontend/graphql/campaigns";
import useMutation from "~frontend/util/useMutation";

import { CampaignForView } from "../types";
import RoiTestSummary from "./RoiTestSummary";

export default function RoiTestAutosendPending({
  campaign,
}: {
  campaign: CampaignForView;
}): ReactElement {
  const confirm = useConfirm();

  const [canceling, setCanceling] = useState(false);

  const [cancelCampaignRoiTest] = useMutation<
    CancelCampaignRoiTestMutation,
    CancelCampaignRoiTestMutationVariables
  >(CANCEL_CAMPAIGN_ROI_TEST, {
    variables: { input: { campaignId: campaign.id } },
    onErrorSnackbarMessage: "Failed to cancel ROI test",
  });

  const handleCancelTest = useCallback(async () => {
    if (canceling) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Cancel ROI test",
      description: "Are you sure you want to cancel the ROI test?",
      confirmationText: "Cancel ROI test",
      cancellationText: "No",
    });
    if (!confirmed) {
      return;
    }

    setCanceling(true);
    await cancelCampaignRoiTest();
    setCanceling(false);
  }, [canceling, confirm, cancelCampaignRoiTest]);

  return (
    <Box>
      {canceling ? (
        <Loading />
      ) : (
        <>
          <RoiTestSummary campaign={campaign} />
          <Typography>
            If you would like to override this to send or schedule the rest of
            your messages, please{" "}
            <LinkButton onClick={handleCancelTest}>cancel your test</LinkButton>
            .
          </Typography>
        </>
      )}

      <FlexBox gap={1} sx={{ width: "400px", pt: 1 }}>
        <Button
          disabled
          variant="contained"
          sx={{
            minWidth: "150px",
          }}
        >
          Preview Text
        </Button>
        <DropdownButton width="150px" label="Send" disabled menuItems={[]} />
        <DropdownButton
          width="150px"
          label="Schedule"
          disabled
          menuItems={[]}
        />
      </FlexBox>
    </Box>
  );
}
